import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, GraduationCap, Users } from "lucide-react";
import { PageDecor } from "../components/decor/PageDecor";
import { TEAM } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { useReglages } from "../lib/useReglages";
import { thumb, thumbWide, fallbackToFull } from "../lib/imageThumb";

const PILIERS = [
  {
    icon: Heart,
    title: "L'humain d'abord",
    text: "Une école à taille humaine où chaque élève est reconnu, encouragé et poussé à donner le meilleur de lui-même. On ne forme pas des exécutants — on accompagne des personnes.",
  },
  {
    icon: GraduationCap,
    title: "Une pédagogie exigeante",
    text: "Formation continue de nos professeurs auprès des meilleurs (New York, Broadway Dance Center, Championnats du monde). Chaque cours est pensé, chaque progression est suivie.",
  },
  {
    icon: Sparkles,
    title: "L'art comme célébration",
    text: "Danse, chant, théâtre, comédie musicale — nous célébrons toutes les formes d'expression artistique. Un spectacle de fin d'année réunit chaque année élèves, familles et amis dans la joie.",
  },
  {
    icon: Users,
    title: "Une communauté",
    // Le nombre d'élèves est lu en direct dans la console (GET /api/reglages) : il n'est
    // plus écrit dans le site, donc plus jamais faux.
    text: (nb) => `Enfants dès 2 ans, ados, adultes, seniors — ${nb} élèves qui composent une vraie famille. On vient pour un cours, on reste pour une communauté.`,
  },
];

const FOUNDERS = TEAM.filter((p) => p.name === "Noémie Vaney" || p.name === "Miller Sacon");

export default function Ecole() {
  const { eleves_actifs: nbEleves } = useReglages();
  useSEO({
    title: "L'école 1000 ARTS — Notre histoire, notre pédagogie · Cugy VD",
    description: "Découvre 1000 ARTS : une école de danse à taille humaine à Cugy VD, portée par Noémie Vaney et Miller Sacon. Comédie musicale, hip-hop, chant, ballet — 15 disciplines et une vraie famille, portées par un projet artistique passionné.",
    canonical: "/ecole",
  });

  return (
    <div data-testid="page-ecole" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      {/* ============= HERO — avec bulles bleues + photo à droite ============= */}
      <section className="relative overflow-hidden pb-24 -mt-32 pt-40">
        {/* Bulles bleues décoratives (blur) */}
        <div className="absolute -top-10 -right-32 w-[520px] h-[520px] hero-shape pointer-events-none" style={{ background: "var(--primary-soft)" }} aria-hidden="true" />
        <div className="absolute top-64 -left-32 w-[380px] h-[380px] hero-shape pointer-events-none" style={{ opacity: 0.35 }} aria-hidden="true" />
        <div className="absolute bottom-0 right-1/3 w-[220px] h-[220px] hero-shape pointer-events-none" style={{ opacity: 0.25 }} aria-hidden="true" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          {/* Colonne texte (7/12) */}
          <div className="md:col-span-7 relative z-10">
            <div className="eyebrow mb-3">L'école 1000 ARTS</div>
            <h1 className="font-display tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl">
              Une école pensée{" "}<br className="hidden md:inline" />
              pour <span style={{ color: "var(--primary)" }}>toutes et tous</span>.
            </h1>
            <p className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl leading-tight text-[var(--text)] opacity-80">
              Quel que soit ton âge, ton niveau ou ton rêve.
            </p>
            <p className="mt-8 text-base md:text-lg text-[var(--muted)] max-w-2xl leading-relaxed">
              Fondée par Noémie Vaney et Miller Sacon, deux artistes passionnés formés en Suisse et à New York, 1000 ARTS est née d'un rêve simple&nbsp;: créer un lieu où l'art se vit comme une célébration, où chaque enfant, chaque ado, chaque adulte trouve sa place. Un endroit exigeant sur la qualité artistique, et infiniment bienveillant sur l'humain.
            </p>
          </div>

          {/* Colonne photo (5/12) — portrait 3:4 avec bordures arrondies */}
          <div className="md:col-span-5 relative z-10">
            <div
              className="rounded-3xl overflow-hidden shadow-2xl relative"
              style={{ aspectRatio: "3/4", background: "var(--bg-soft)" }}
            >
              <img
                src={thumbWide("/img/2cb47f3977.webp")} onError={fallbackToFull("/img/2cb47f3977.webp")}
                alt="Noémie Vaney et Miller Sacon — fondateurs de 1000 ARTS — sur scène en tenue de scène"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="ecole-hero-photo"
              />
              {/* Badge floating */}
              <div
                className="absolute -bottom-4 -left-4 rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3"
                style={{ background: "#fff" }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}>
                  <Heart size={18} />
                </div>
                <div>
                  <div className="text-xs text-[var(--muted)] leading-none">Nos fondateurs</div>
                  <div className="font-semibold text-sm leading-tight mt-0.5">Noémie &amp; Miller</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= 4 PILIERS PÉDAGOGIE ============= */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-12 mt-24" data-testid="ecole-piliers">
        <div className="absolute -top-20 right-0 w-[300px] h-[300px] hero-shape pointer-events-none" style={{ opacity: 0.2 }} aria-hidden="true" />
        <div className="relative z-10">
        <div className="eyebrow mb-3">Notre pédagogie</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">Ce qui fait la différence chez 1000 ARTS</h2>
        <div className="mt-10 mb-8 max-w-4xl mx-auto rounded-[32px] overflow-hidden shadow-2xl" data-testid="ecole-pedagogie-photo">
          <img
            src={thumbWide("/img/2a33d3a92b.webp")} onError={fallbackToFull("/img/2a33d3a92b.webp")}
            alt="Notre pédagogie chez 1000 ARTS — cours et transmission"
            className="w-full h-[300px] md:h-[420px] object-cover"
            loading="lazy"
          />
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {PILIERS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`ecole-pilier-${i}`}
                className="rounded-3xl p-8 flex gap-5 items-start"
                style={{ background: "var(--primary-tint)", border: "1.5px solid rgba(0,198,206,0.18)" }}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--primary)", color: "#fff" }}>
                  <Icon size={26} />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl leading-tight mb-2">{p.title}</h3>
                  <p className="text-sm md:text-base text-[var(--text)] leading-relaxed">
                    {typeof p.text === "function" ? p.text(nbEleves || 230) : p.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {/* ============= JEUNESSE + SPORT ============= */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-12 mt-24" data-testid="ecole-jeunesse-sport">
        <div
          className="rounded-[32px] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12"
          style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)", boxShadow: "0 24px 60px -32px rgba(15,27,45,0.28)" }}
        >
          <img
            src="/img/jeunesse-et-sport.png"
            alt="Logo Jeunesse+Sport — Confédération suisse"
            className="w-24 h-24 md:w-32 md:h-32 object-contain shrink-0"
            loading="lazy"
            data-testid="ecole-js-logo"
          />
          <div>
            <div className="eyebrow mb-3">Reconnaissance officielle</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">
              1000 ARTS &amp; Jeunesse+Sport
            </h2>
            <p className="text-base text-[var(--text)] leading-relaxed mt-5 max-w-2xl">
              1000 ARTS intègre désormais le programme <strong>Jeunesse+Sport (J+S)</strong> de la
              Confédération : des coachs formés, un encadrement de qualité et toujours plus d'outils
              pour accompagner nos jeunes danseurs.
            </p>
            <p className="font-display text-xl md:text-2xl tracking-tight mt-5" style={{ color: "var(--primary)" }}>
              Grandir. Progresser. S'épanouir par la danse.
            </p>
          </div>
        </div>
      </section>


      {/* ============= NOS FONDATEURS ============= */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-12 mt-24 overflow-visible" data-testid="ecole-founders">
        <div className="absolute -top-10 -left-32 w-[380px] h-[380px] hero-shape pointer-events-none" style={{ opacity: 0.22 }} aria-hidden="true" />
        <div className="relative z-10">
        <div className="eyebrow mb-3">Nos fondateurs</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">Deux artistes, un rêve commun</h2>
        <p className="mt-4 text-lg text-[var(--muted)] max-w-3xl">
          Noémie et Miller se sont rencontrés autour de la danse et ont bâti 1000 ARTS pour partager leur passion avec les habitants du Gros-de-Vaud et d'ailleurs.
        </p>

        <div className="mt-12 space-y-16">
          {FOUNDERS.map((f, i) => (
            <article
              key={f.name}
              data-testid={`founder-${f.name.split(" ")[0].toLowerCase()}`}
              className="grid md:grid-cols-5 gap-8 items-start"
            >
              {/* Image portrait — 2 colonnes */}
              <div className={`md:col-span-2 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div
                  className="rounded-3xl overflow-hidden shadow-xl"
                  style={{ aspectRatio: "3/4", background: "var(--bg-soft)" }}
                >
                  <img src={thumb(f.image)} onError={fallbackToFull(f.image)} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              {/* Bio — 3 colonnes */}
              <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="eyebrow" style={{ color: "var(--primary)" }}>{f.role}</div>
                <h3 className="font-display text-3xl md:text-5xl tracking-tight mt-2 mb-6">{f.name}</h3>
                <div className="space-y-4 text-[var(--text)] leading-relaxed">
                  {f.bio_long.split("\n\n").map((p, k) => (
                    <p key={k}>{p}</p>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        </div>
      </section>

      {/* ============= CTA équipe complète ============= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24">
        <div
          className="rounded-3xl p-10 md:p-16 text-center"
          style={{ background: "linear-gradient(135deg, var(--primary) 0%, #00A8B0 100%)", color: "#fff" }}
          data-testid="ecole-team-cta"
        >
          <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>L'équipe</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">
            Découvre toute l'équipe pédagogique
          </h2>
          <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto">
            Une équipe de passionnés, formés auprès des meilleurs en Suisse et à l'international, qui transmettent leur art avec exigence et bienveillance.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/professeurs"
              data-testid="cta-professeurs"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold shadow-md hover:shadow-xl hover:scale-[1.02] transition-all"
              style={{ background: "#fff", color: "var(--primary)" }}
            >
              Voir toute l'équipe <ArrowRight size={16} />
            </Link>
            <Link
              to="/cours"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-[var(--primary)] transition-colors"
            >
              Explorer les cours
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
